import { useState, useCallback } from 'react';
import { stockyApi } from '../components/misc/StockyApi';

const PARTIAL_SEARCH_MIN_LENGTH = 3;

export const useMarketplaceSearch = (authUser, initialFilters) => {
  const [searchFilters, setSearchFilters] = useState(initialFilters);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchHasExecuted, setSearchHasExecuted] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchMeta, setSearchMeta] = useState({
    title: '',
    sku: '',
    limit: 20,
    page: 1,
    pageSize: 20,
    total: 0,
  });

  const handleSearchMarketplace = useCallback(async (pageOverride = 1) => {
    if (!authUser) return;
    const title = String(searchFilters.titleInput || '').trim();
    const sku = String(searchFilters.skuInput || '').trim();

    if (!title && !sku) {
        return { warning: 'Ingresá un título o un SKU para buscar productos.' };
    }

    const hasShortTitle = title.length > 0 && title.length < PARTIAL_SEARCH_MIN_LENGTH;
    const hasShortSku = sku.length > 0 && sku.length < PARTIAL_SEARCH_MIN_LENGTH;
    if (hasShortTitle || hasShortSku) {
      const fields = [
        hasShortTitle ? 'titulo' : null,
        hasShortSku ? 'SKU' : null,
      ].filter(Boolean);
      return { warning: `Para búsqueda parcial por ${fields.join(' y ')}, ingresá al menos ${PARTIAL_SEARCH_MIN_LENGTH} caracteres.` };
    }

    setSearchLoading(true);
    setSearchError(null);
    try {
      const nextPage = Math.max(1, Number(pageOverride || 1));
      const response = await stockyApi.searchMarketplaceSyncSelection(authUser, {
        marketplace: searchFilters.marketplace,
        title,
        sku,
        limit: 20,
        page: nextPage,
        sellerId: searchFilters.sellerId,
        status: searchFilters.remoteStatus,
      });
      const results = Array.isArray(response?.data?.data) ? response.data.data : [];
      setSearchResults(results);
      setSearchMeta({
        title: String(response?.data?.title || title),
        sku: String(response?.data?.sku || sku),
        limit: Number(response?.data?.limit || 20),
        page: Number(response?.data?.page || nextPage),
        pageSize: Number(response?.data?.pageSize || 20),
        total: Number(response?.data?.total || results.length),
        totalPages: Number(response?.data?.totalPages || 0),
      });
      setSearchPage(Number(response?.data?.page || nextPage));
      setSearchHasExecuted(true);
      setSearchFilters((prev) => ({
        ...prev,
        titleInput: title,
        skuInput: sku,
        title,
        sku,
      }));
      return { success: true };
    } catch (error) {
      console.error('Error searching marketplace products:', error);
      setSearchError({
        title: 'No pudimos buscar productos',
        content:
          error?.response?.data?.message ||
          'Intentá nuevamente en unos segundos.',
      });
      return { success: false, error };
    } finally {
      setSearchLoading(false);
    }
  }, [authUser, searchFilters]);

  return {
    searchFilters,
    setSearchFilters,
    searchLoading,
    searchError,
    setSearchError,
    searchResults,
    setSearchResults,
    searchHasExecuted,
    setSearchHasExecuted,
    searchPage,
    setSearchPage,
    searchMeta,
    handleSearchMarketplace,
  };
};
